import styled from "@emotion/styled";
import { forwardRef, useCallback, useState, type HTMLAttributes, type MouseEventHandler } from "react";

import { classNames } from "@skilitics/designer-ui-components";

import { useSelectedNodeId } from "../selection/useSelectedNode";

const Container = styled.div`
  &.selected {
    outline: solid 2px ${({ theme }) => theme.primary};
    z-index: 1;
  }

  // Using hovered class instead of css:hover to prevent nested hover effects.
  &.hovered:not(.selected) {
    outline: dashed 2px ${({ theme }) => theme.primary};
    z-index: 1;
  }
`;

export type SelectableNodeWrapperProps = HTMLAttributes<HTMLDivElement> & {
  nodeId: number;
};

export const SelectableNodeWrapper = forwardRef<HTMLDivElement, SelectableNodeWrapperProps>(
  ({ nodeId, ...rest }, ref) => {
    const [selectedNodeId] = useSelectedNodeId();
    const [_, setSelectedNodeId] = useSelectedNodeId();
    const [hovered, setHovered] = useState(false);

    const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
      (e) => {
        e.stopPropagation();
        setSelectedNodeId(nodeId);
      },
      [nodeId, setSelectedNodeId],
    );

    const handleMouseOver: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
      e.stopPropagation();
      setHovered(true);
    }, []);

    const handleMouseOut: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
      e.stopPropagation();
      setHovered(false);
    }, []);

    return (
      <Container
        ref={ref}
        className={classNames({
          selected: selectedNodeId === nodeId,
          hovered,
        })}
        onClick={handleClick}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        {...rest}
      />
    );
  },
);
SelectableNodeWrapper.displayName = "SelectableNodeWrapper";
